import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { menuOfPath } from "./permissions";

// The logged-in user's roadmap for Users › Activity (UserActivityTrail): pages opened (sent on open, the time spent on
// leave), clicks on buttons / links / tabs (their label only — typed values are never read) and logout. Events are sent
// in batches every 15 s, on a page change and when the tab is closed (fetch keepalive, so the last batch still goes).

const FLUSH_MS = 15000;
const MAX_BATCH = 200;
const CLICKABLE = "button, a, [role=tab], [data-track]";

let queue = [];
let page = null; // { path, title, start }
let timer = null;

const now = () => new Date(Date.now());
const iso = (date) => date.toISOString().slice(0, 19) + "Z";
const cut = (text, max) =>
  String(text ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
const titleOf = (path) => menuOfPath(path)?.menuName || cut(document.title, 200);

/** The label of a clicked element: data-track, aria-label, title, then its text (max 100). */
export function clickLabel(el) {
  return cut(el.getAttribute("data-track") || el.getAttribute("aria-label") || el.getAttribute("title") || el.textContent, 100);
}

function push(event) {
  queue.push(event);
  if (queue.length >= MAX_BATCH) flush();
}

/** Closes the current page: a PageLeave with its open time and the seconds spent (the API fills the opened PageView). */
function closePage() {
  if (!page) return;
  push({
    eventType: "PageLeave",
    pagePath: page.path,
    pageTitle: page.title,
    durationSeconds: Math.round((now() - page.start) / 1000),
    eventTime: iso(page.start),
  });
  page = null;
}

/** Sends the queued events (best effort; a failed batch is dropped, never retried in a loop). */
export function flush() {
  if (!queue.length || !sessionStorage.getItem("session_userName")) return;
  const events = queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({
    userName: sessionStorage.getItem("session_userName"),
    userId: Number(sessionStorage.getItem("session_userId")) || 0,
    airlineCode: sessionStorage.getItem("session_airlineCode") ?? "",
    sessionId: sessionStorage.getItem("session_sessionId") ?? "",
    events,
  });
  try {
    fetch(`${String(process.env.REACT_APP_API_BASE_URL ?? "").replace(/\/$/, "")}/UserActivityTrail/log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // the trail must never break a page
  }
}

/** A route was opened: closes the previous page, records this one at once (shown as "on the page now") and starts timing it. */
export function trackPage(path) {
  if (page && page.path === path) return;
  closePage();
  page = { path, title: titleOf(path), start: now() };
  push({ eventType: "PageView", pagePath: page.path, pageTitle: page.title, eventTime: iso(page.start) });
  flush();
}

function onClick(e) {
  // The user's clicks are switched off (Users › Activity); the API drops them too.
  if (sessionStorage.getItem("session_trackClicks") === "0") return;
  const el = e.target instanceof Element ? e.target.closest(CLICKABLE) : null;
  if (!el) return;
  const target = clickLabel(el);
  if (!target) return;
  const path = page?.path ?? window.location.pathname;
  push({ eventType: "Click", pagePath: path, pageTitle: page?.title ?? titleOf(path), target, eventTime: iso(now()) });
}

function onPageHide() {
  closePage();
  flush();
}

function onVisibility() {
  if (document.visibilityState === "hidden") flush();
}

/** Logout (button or idle): records the page and the logout, sends them; call before sessionStorage is cleared. */
export function trackLogout(reason = "Logout") {
  const path = page?.path ?? window.location.pathname;
  closePage();
  push({ eventType: "Logout", pagePath: path, pageTitle: titleOf(path), target: cut(reason, 100), eventTime: iso(now()) });
  flush();
}

export function startTracker() {
  if (timer) return;
  document.addEventListener("click", onClick, true);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);
  timer = setInterval(flush, FLUSH_MS);
}

export function stopTracker() {
  if (!timer) return;
  document.removeEventListener("click", onClick, true);
  window.removeEventListener("pagehide", onPageHide);
  document.removeEventListener("visibilitychange", onVisibility);
  clearInterval(timer);
  timer = null;
  onPageHide();
}

/** Rendered once inside the logged-in layout: starts the tracker and records each route change. */
export function ActivityTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    startTracker();
    return stopTracker;
  }, []);
  useEffect(() => {
    trackPage(pathname);
  }, [pathname]);
  return null;
}
